import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Stack,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { formatCurrency } from '../../utils/formatting';
import { computeEventPayout } from '../../services/eventService';

const EPSILON = 0.5;

/** Event hak ediş ödemesi aşamaları */
export const PAYMENT_STAGE_OPTIONS = [
  { value: 'prepayment', label: 'Ön Ödeme' },
  { value: 'interim', label: 'Ara Ödeme' },
  { value: 'final', label: 'Son Ödeme' },
  { value: 'other', label: 'Diğer' },
];

const Row = ({ label, value, bold, color }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.25 }}>
    <Typography variant="body2" sx={{ fontWeight: bold ? 700 : 400 }}>
      {label}
    </Typography>
    <Typography
      variant="body2"
      sx={{ fontWeight: bold ? 700 : 400, color: color || 'inherit' }}
    >
      {formatCurrency(value)}
    </Typography>
  </Box>
);

/**
 * Sale Report Pay modal:
 * - Ödeme aşaması (ön / ara / son ödeme)
 * - Tutar + açıklama
 */
const PaymentModal = ({ open, onClose, event, eventStatsData, onSubmit }) => {
  const stats = eventStatsData || {};
  const { netOwed, paid: alreadyPaid, remainingToPay: remainingBefore } = useMemo(
    () => computeEventPayout(event, stats),
    [stats, event],
  );

  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [paymentStage, setPaymentStage] = useState('prepayment');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setAmount('');
    setNote('');
    setPaymentStage('prepayment');
  }, [open, event?.id]);

  const parsedAmount = Number(amount) || 0;
  const remainingAfter = remainingBefore - parsedAmount;
  const stageLabel =
    PAYMENT_STAGE_OPTIONS.find((o) => o.value === paymentStage)?.label || paymentStage;

  const handleFillRemaining = () => {
    setAmount(String(Math.max(remainingBefore, 0)));
  };

  const handleSave = async () => {
    if (parsedAmount <= 0) return;
    setSaving(true);
    try {
      const newPaidAmount = alreadyPaid + parsedAmount;
      const isPaid = netOwed - newPaidAmount <= EPSILON;
      const payNote = [stageLabel, note.trim()].filter(Boolean).join(' — ');
      await onSubmit({
        actionType: 'pay',
        paymentType: paymentStage,
        paymentStage,
        paidAmount: newPaidAmount,
        paidNow: parsedAmount,
        payNote,
        isPaid,
      });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Ödeme Yap</DialogTitle>
      <DialogContent dividers>
        <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
          {event?.name}
        </Typography>

        <Box sx={{ bgcolor: '#FAFAFA', borderRadius: 1, p: 1.5, mb: 2 }}>
          <Row label="Net Hak Ediş" value={netOwed} bold color="#1976d2" />
          <Row label="Ödenen" value={alreadyPaid} />
          <Row
            label="Kalan"
            value={remainingBefore}
            bold
            color={remainingBefore > EPSILON ? '#d32f2f' : '#2e7d32'}
          />
        </Box>

        <Stack spacing={2}>
          <FormControl fullWidth size="small" required>
            <InputLabel id="payment-stage-label">Ödeme Türü</InputLabel>
            <Select
              labelId="payment-stage-label"
              label="Ödeme Türü"
              value={paymentStage}
              onChange={(e) => setPaymentStage(e.target.value)}
            >
              {PAYMENT_STAGE_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Stack direction="row" spacing={1} alignItems="flex-start">
            <TextField
              label="Tutar (TL)"
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              fullWidth
              size="small"
              required
              inputProps={{ min: 0, step: '0.01' }}
            />
            <Button
              onClick={handleFillRemaining}
              variant="outlined"
              size="medium"
              sx={{ whiteSpace: 'nowrap', mt: 0.25 }}
            >
              Kalanı doldur
            </Button>
          </Stack>

          <TextField
            label="Açıklama"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            fullWidth
            multiline
            minRows={2}
            size="small"
            placeholder="Örn: 1. taksit, banka dekont notu..."
          />

          {parsedAmount > 0 && (
            <Box sx={{ bgcolor: '#F0F7FF', borderRadius: 1, p: 1.5 }}>
              <Row
                label="Bu ödeme sonrası kalan"
                value={remainingAfter}
                bold
                color={remainingAfter > EPSILON ? '#d32f2f' : '#2e7d32'}
              />
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          İptal
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={saving || parsedAmount <= 0}
          startIcon={saving ? <CircularProgress size={16} /> : null}
        >
          Kaydet
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PaymentModal;
